import ScrollTrigger from 'gsap/ScrollTrigger';
import { initTelInputs } from '../components/telInput';

let loaded = false;

function loadScript() {
  if (!loaded) {
    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    document.body.appendChild(script);
    loaded = true;
  }

  return new Promise((resolve) => {
    if (window.hbspt) {
      resolve();
      return;
    }

    const interval = setInterval(() => {
      if (window.hbspt) {
        clearInterval(interval);
        resolve();
        return;
      }
    }, 500);
  });
}

function getHsFormConversionPage(fallbackValue) {
  if (document.referrer === undefined || document.referrer.length === 0) {
    return fallbackValue;
  } else {
    return String(document.referrer);
  }
}

export async function embedHsForm(
  targetSelector,
  hsFormParams = {},
  redirectToOnSubmit = null,
) {
  attachFallbackFormSubmissionHandlers();
  const target = document.querySelector(targetSelector);
  if (!target) return;

  ScrollTrigger.create({
    trigger: target,
    start: 'top-=1000 bottom',
    once: true,
    onEnter: async () => {
      const confirmFormReady = buildFormReadyConfirmation();
      await loadScript();
      initForm(targetSelector, hsFormParams, redirectToOnSubmit,
        confirmFormReady);
    },
  });
}

async function initForm(
  targetSelector,
  hsFormParams,
  redirectToOnSubmit,
  onFormReadyCallback
) {
  const target = document.querySelector(targetSelector);
  redirectToOnSubmit = redirectToOnSubmit || target.dataset.redirectToOnSubmit;

  const requiredParams = ['region', 'portalId', 'formId', 'target'];
  const params = { ...hsFormParams, ...target.dataset, target: targetSelector };
  for (let param of requiredParams) {
    if (!Object.keys(params).includes(param)) {
      console.error(
        `Required param ${param} is not set for ${targetSelector} HsForm`,
      );
      return;
    }
  }

  const embeddedHsForm = hbspt.forms.create({
    ...params,
    css: '',
    onFormReady: function (form) {
      if (typeof onFormReadyCallback === 'function') {
        onFormReadyCallback();
      }
      initTelInputs();
      let formElement = null;
      let regex = /hsForm/;
      if (!!form && form.nodeType === Node.ELEMENT_NODE) {
        formElement = form;
      } else if (!!form && typeof form === 'object') {
        for (var i = 0; i < form.length; i++) {
          if (!!form[i].id && regex.test(form[i].id)) {
            formElement = form[i];
            break;
          }
        }
      } else {
        return;
      }
      console.log({ formElement });
      const textarea = formElement.querySelector('.hs-fieldtype-textarea');
      textarea.addEventListener('keyup', (element) => {
        if (!!element.target) {
          element.target.style.height = element.target.scrollHeight + 'px';
        }
      });

      embeddedHsForm.setFieldValue(
        'conversion_page',
        getHsFormConversionPage('No conversion page'),
      );

      try {
        var conversionPageUrl = getHsFormConversionPage('');
        if (!!conversionPageUrl && conversionPageUrl.length > 0) {
          embeddedHsForm.getContext().pageTitle = '';
          embeddedHsForm.getContext().pageUrl = conversionPageUrl;
        }
      } catch (e) {
        console.error(e);
      }

      hideSpecifiedFields(formElement, params);
      attachStyledFormIntefrace(formElement, params, embeddedHsForm);
    },
    onFormSubmitted: () => {
      if (redirectToOnSubmit) window.location = redirectToOnSubmit;
    }
  });
  window.hsFormTestfield = embeddedHsForm;
}

/**
 * Hides form fields, specified by data-hidden-fields attribute.
 * 
 * @param {HTMLFormElement} formElement 
 * @param {Object} params 
 * @returns 
 */
function hideSpecifiedFields(formElement, params) {
  if (!Object.hasOwn(params, 'hiddenFields')) {
    return;
  }

  (params.hiddenFields ?? '').split(',')
    .filter((it) => typeof it === 'string')
    .map((it) => it.trim())
    .filter((it) => it.length > 0)
    .map((it) => '.field.' + it)
    .map((it) => formElement.querySelector(it))
    .filter((it) => it !== undefined && it !== null)
    .forEach((it) => {
      it.setAttribute('hidden', 'hidden');
    });
}

/**
 * 
 * @param {HTMLFormElement} formElement hubspot form
 * @param {Object} params hubspot form params
 * @param {Object} hsFormInstance hubspot form instance
 */
function attachStyledFormIntefrace(formElement, params, hsFormInstance) {
  if (!formElement || !params || !params.formId || !document) {
    return;
  }

  const formInterface = document.querySelector('form[data-hs-target-form-id="' + params.formId + '"]');
  if (!formInterface) {
    return;
  }

  setInterval(function() {
    passHsFormFeedbackMessages();
  }, 1000);

  formInterface.querySelectorAll('input,textarea').forEach((input) => {
    const tgtInputName = input.name;
    const hsInput = tgtInputName
      ? formElement.querySelector('[name="' + tgtInputName + '"]')
      : false;

    if (!hsInput) {
      console.warn('Field ' + tgtInputName + ' not found');
      return;
    }

    /**
     * Field updater event handler
     * 
     * @param {Event} event any field event
     */
    function fieldUpdater(event) {
      hsFormInstance.setFieldValue(tgtInputName, event.target.value);
      passHsFormFeedbackMessages();
    }

    input.addEventListener('change', fieldUpdater);
    input.addEventListener('keyup', fieldUpdater);
  });
  formInterface.setAttribute('data-hs-handler', 'true');

  function formSubmitHandler() {
    const hsSubmitBtn = formElement.querySelector('[type="submit"]');
    if (hsSubmitBtn) {
      hsSubmitBtn.click();
    }
    submitBtn.focus();
    passHsFormFeedbackMessages();
  }

  const submitBtn = formInterface.querySelector('.wll-form__submit');
  if (submitBtn) {
    submitBtn.addEventListener('click', formSubmitHandler);
    formInterface.setAttribute('data-hs-handler', 'true');
    // Using the same handler, emulating the click
    formInterface.addEventListener('submit', formSubmitHandler);
  }

  function passHsFormFeedbackMessages() {
    if (!formElement) {
      return;
    }

    const hsFormFields = formElement.querySelectorAll('.hs-form-field');
    hsFormFields.forEach((fieldBlock) => {
      const hsInputElement = fieldBlock.querySelector('.hs-input');
      const fieldName = hsInputElement ? hsInputElement.name : null;
      const interfaceField = formInterface.querySelector(
        '[name=\'' + fieldName + '\']'
      );
      const interfaceErrorTextElement = interfaceField
        ? interfaceField.closest('label').querySelector('.text-input__error')
        : null;
      const errorMessages = fieldBlock.querySelectorAll('.hs-error-msg');

      if (errorMessages.length > 0) {
        // Displaying messages from HS form
        const messagesArray = [];
        errorMessages.forEach((value, _key, _parent) => {
          messagesArray.push(value.textContent);
        });
        if (interfaceErrorTextElement) {
          interfaceField.classList.add('has-user-invalid');
          interfaceErrorTextElement.textContent = messagesArray.join(', ');
        }
      } else {
        // Removing error messages from interface
        if (interfaceField) {
          interfaceField.classList.remove('has-user-invalid');
        }
        if (interfaceErrorTextElement) {
          interfaceErrorTextElement.textContent = '';
        }
      }
    });
  }
}

function buildFormReadyConfirmation(timeout = 10) {
  var isFormReady = false;
  setTimeout(function () {
    if (!isFormReady) {
      sendFrontendFormIssueReport();
    }
  }, timeout * 1000);

  return function () {
    isFormReady = true;
  }
}

function sendFrontendFormIssueReport(timeout = 10) {
  fetch('/wp-json/pflb/v1/reporting/frontend/form/', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message: 'HubSpot form failed to render in ' + timeout + ' seconds at ' + window.location.href,
    })
  })
    .catch((reason) => {
      console.error(reason);
    });
}

function attachFallbackFormSubmissionHandlers() {
  document.querySelectorAll('form[data-hs-target-form-id]')
    .forEach((/** @type {HTMLFormElement} */ formElement) => {
      function formSubmitHandler(/** @type {Event} */ event) {
        // Not passing the click/submit here, just fallback behavior
        event.preventDefault();
        if (formElement.dataset.hsHandler) {
          return;
        }
        fallbackFormSubmission(new FormData(formElement));
      }

      const submitButton = formElement.querySelector('button.wll-form__submit');
      if (submitButton) {
        submitButton.addEventListener('click', formSubmitHandler);
      } else {
        console.warn('Found no submit button for the form', formElement);
      }
      formElement.addEventListener('submit', formSubmitHandler);
    });
}

/**
 * Sends FormData as notification to a fallback wp-json endpoint.
 * 
 * @param {FormData} formData Data to 
 */
function fallbackFormSubmission(formData) {
  const dataObj = {};
  formData.forEach((value, key) => {
    dataObj[key] = value;
  });
  try {
    const payload = JSON.stringify(dataObj);
    if (typeof payload === 'string' && payload.length > 2) {
      fetch('/wp-json/pflb/v1/form/fallback', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload
      })
        .catch((reason) => {
          console.error(reason);
        });
    }
  } catch (e) {
    console.error(e);
  }
}
